import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin, Mail, Users } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LocationsPage = () => {
  const { canCreate, canEdit, canDelete, hasLocationAccess } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', email_to: '', email_cc: '' });

  // Kullanıcının yetkili olduğu lokasyonları filtrele
  const authorizedLocations = locations.filter(loc => hasLocationAccess(loc.id));

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Lokasyonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({ 
        name: location.name, 
        description: location.description || '', 
        email_to: location.email_to || location.email || '', // Eski email alanından migration
        email_cc: location.email_cc || '' 
      });
    } else {
      setEditingLocation(null);
      setFormData({ name: '', description: '', email_to: '', email_cc: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await axios.put(`${API}/locations/${editingLocation.id}`, formData);
        toast.success('Lokasyon güncellendi');
      } else {
        await axios.post(`${API}/locations`, formData);
        toast.success('Lokasyon eklendi');
      }
      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Lokasyon silinsin mi?', description: 'Bu lokasyonu silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/locations/${id}`);
      toast.success('Lokasyon silindi');
      fetchLocations();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="locations-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lokasyonlar</h1>
          <p className="text-slate-500 mt-1">AVM lokasyonlarını yönetin</p>
        </div>
        {canCreate('locations') && (
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-location-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Lokasyon Ekle
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Lokasyon Adı</TableHead>
                <TableHead className="font-semibold">Açıklama</TableHead>
                <TableHead className="font-semibold">E-posta</TableHead>
                <TableHead className="font-semibold text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {authorizedLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <MapPin className="h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">Henüz lokasyon eklenmemiş</p>
                      {canCreate('locations') && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleOpenDialog()}
                        >
                          İlk lokasyonu ekle
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                authorizedLocations.map((location) => (
                  <TableRow key={location.id} className="table-row-hover">
                    <TableCell className="font-medium">{location.name}</TableCell>
                    <TableCell className="text-slate-500">{location.description || '-'}</TableCell>
                    <TableCell>
                      {(location.email_to || location.email) ? (
                        <div className="space-y-1">
                          <span className="flex items-center gap-1 text-sm text-slate-600">
                            <Mail className="h-3 w-3 text-blue-500" />
                            <span className="font-medium">To:</span> {location.email_to || location.email}
                          </span>
                          {location.email_cc && (
                            <span className="flex items-center gap-1 text-sm text-slate-500">
                              <Users className="h-3 w-3 text-slate-400" />
                              <span className="font-medium">CC:</span> {location.email_cc}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit('locations') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(location)}
                            data-testid={`edit-location-${location.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete('locations') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(location.id)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-location-${location.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? 'Lokasyon Düzenle' : 'Yeni Lokasyon'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Lokasyon Adı</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Örn: A Blok Giriş"
                  required
                  data-testid="location-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Açıklama (Opsiyonel)</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Lokasyon hakkında kısa açıklama"
                  data-testid="location-description-input"
                />
              </div>
              
              {/* E-posta Alanları */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Mail className="h-4 w-4" />
                  E-posta Bildirimleri
                </div>
                <p className="text-xs text-slate-500 -mt-2">
                  Eksiklik raporu gönderilirken bu e-posta adresleri kullanılacaktır. 
                  Birden fazla e-posta için <strong>;</strong> ile ayırın (Outlook formatı).
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="email_to" className="flex items-center gap-1">
                    <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs font-medium">TO</span>
                    Alıcılar
                  </Label>
                  <Textarea
                    id="email_to"
                    value={formData.email_to}
                    onChange={(e) => setFormData({ ...formData, email_to: e.target.value })}
                    placeholder="örnek@firma.com; diger@firma.com"
                    className="min-h-[60px] text-sm"
                    data-testid="location-email-to-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email_cc" className="flex items-center gap-1">
                    <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-xs font-medium">CC</span>
                    Bilgi İçin
                  </Label>
                  <Textarea
                    id="email_cc"
                    value={formData.email_cc}
                    onChange={(e) => setFormData({ ...formData, email_cc: e.target.value })}
                    placeholder="yonetici@firma.com; denetci@firma.com"
                    className="min-h-[60px] text-sm"
                    data-testid="location-email-cc-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-location-btn">
                {editingLocation ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsPage;
